import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  Home,
  Wallet, 
  BarChart3, 
  Bot, 
  Target,
  Menu,
  X,
  ChevronLeft,
  LogOut,
  User,
  Play,
  Square,
  Loader2,
  Flame,
  Zap,
  Repeat
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { getAuthenticatedAxios, getApiUrl } from '../utils/api';
import { toast } from 'sonner';

const API_URL = getApiUrl();

const Layout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [marketMakerStatus, setMarketMakerStatus] = useState({
    running: false,
    enabled: false,
    loading: false
  });

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Load market maker status
  useEffect(() => {
    loadMarketMakerStatus();
    // Refresh status every 5 seconds
    const interval = setInterval(loadMarketMakerStatus, 5000);
    return () => clearInterval(interval);
  }, []);

  const loadMarketMakerStatus = async () => {
    try {
      const api = getAuthenticatedAxios();
      const response = await api.get(`${API_URL}/api/market-maker/status`);
      if (response.data.success) {
        setMarketMakerStatus({
          running: response.data.running,
          enabled: response.data.enabled,
          loading: false
        });
      }
    } catch (error) {
      console.error('Error loading market maker status:', error);
      setMarketMakerStatus(prev => ({ ...prev, loading: false }));
    }
  };

  const handleMarketMakerToggle = async () => {
    setMarketMakerStatus(prev => ({ ...prev, loading: true }));
    try {
      const api = getAuthenticatedAxios();
      if (marketMakerStatus.running) {
        // Stop market maker
        const response = await api.post(`${API_URL}/api/market-maker/stop`);
        if (response.data.success) {
          toast.success('Market Maker остановлен');
          setMarketMakerStatus(prev => ({ ...prev, running: false, loading: false }));
        } else {
          toast.error('Ошибка при остановке Market Maker');
          setMarketMakerStatus(prev => ({ ...prev, loading: false }));
        }
      } else {
        // Start market maker
        const response = await api.post(`${API_URL}/api/market-maker/start`);
        if (response.data.success) {
          toast.success('Market Maker запущен');
          setMarketMakerStatus(prev => ({ ...prev, running: true, loading: false }));
        } else {
          toast.error('Ошибка при запуске Market Maker');
          setMarketMakerStatus(prev => ({ ...prev, loading: false }));
        }
      }
    } catch (error) {
      console.error('Error toggling market maker:', error);
      toast.error('Ошибка при управлении Market Maker');
      setMarketMakerStatus(prev => ({ ...prev, loading: false }));
    }
  };

  const navigation = [
    { 
      name: 'Home', 
      href: '/', 
      icon: Home,
      description: 'Main dashboard'
    },
    { 
      name: 'Token Analytics', 
      href: '/token-tracker', 
      icon: BarChart3,
      description: 'Holder analysis & P/L'
    },
    { 
      name: 'Wallet Manager', 
      href: '/wallets', 
      icon: Wallet,
      description: 'Generate & manage wallets'
    },
    {
      name: 'Market Maker',
      href: '/market-maker',
      icon: Bot,
      description: 'Automated trading strategy'
    },
    {
      name: 'Cycle Bootstrap',
      href: '/bootstrap',
      icon: Repeat,
      description: 'Sell → transit → fund workers'
    },
    { 
      name: 'Wallet Warmup', 
      href: '/wallet-warmup', 
      icon: Flame,
      description: 'Automatic wallet warming'
    },
    { 
      name: 'BLUM DEX', 
      href: '/blum', 
      icon: Zap,
      description: 'BCL token trading'
    }
  ];

  const isActive = (path) => {
    return location.pathname === path;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile menu button */}
      <div className="lg:hidden fixed top-4 left-4 z-50">
        <button
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="p-2 bg-white rounded-lg shadow-lg"
        >
          {mobileMenuOpen ? (
            <X className="h-6 w-6 text-gray-600" />
          ) : (
            <Menu className="h-6 w-6 text-gray-600" />
          )}
        </button>
      </div>

      {/* Sidebar */}
      <div className={`
        fixed inset-y-0 left-0 z-40 
        ${sidebarOpen ? 'w-72' : 'w-20'} 
        bg-white shadow-xl transition-all duration-300
        ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="p-4 border-b">
            <div className="flex items-center justify-between">
              {sidebarOpen && (
                <div>
                  <h1 className="text-xl font-bold text-gray-900">TON DEX</h1>
                  <p className="text-xs text-gray-500">Trading Platform</p>
                </div>
              )}
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="p-2 hover:bg-gray-100 rounded-lg hidden lg:block"
              >
                <ChevronLeft className={`h-5 w-5 text-gray-600 transition-transform ${!sidebarOpen ? 'rotate-180' : ''}`} />
              </button>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 overflow-y-auto p-4 space-y-2">
            {navigation.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.href);
              
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`
                    flex items-center gap-3 px-4 py-3 rounded-lg transition-all
                    ${active 
                      ? 'bg-blue-600 text-white shadow-lg' 
                      : 'text-gray-700 hover:bg-gray-100'
                    }
                    ${!sidebarOpen && 'justify-center'}
                  `}
                >
                  <Icon className={`h-5 w-5 ${active ? 'text-white' : 'text-gray-600'}`} />
                  {sidebarOpen && (
                    <div className="flex-1">
                      <div className="font-medium">{item.name}</div>
                      <div className={`text-xs ${active ? 'text-blue-100' : 'text-gray-500'}`}>
                        {item.description}
                      </div>
                    </div>
                  )}
                </Link>
              );
            })}
          </nav>

          {/* Footer */}
          {sidebarOpen && (
            <div className="p-4 border-t space-y-3">
              {/* Market Maker Status */}
              <div className="px-3 py-2 bg-gray-50 rounded-lg border border-gray-200">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Bot className="h-4 w-4 text-gray-600" />
                    <span className="text-sm font-medium text-gray-900">Market Maker</span>
                  </div>
                  <div className={`flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium ${
                    marketMakerStatus.running 
                      ? 'bg-green-100 text-green-700' 
                      : 'bg-gray-200 text-gray-600'
                  }`}>
                    <div className={`w-1.5 h-1.5 rounded-full ${
                      marketMakerStatus.running ? 'bg-green-500 animate-pulse' : 'bg-gray-400'
                    }`} />
                    {marketMakerStatus.running ? 'Запущен' : 'Остановлен'}
                  </div>
                </div>
                <button
                  onClick={handleMarketMakerToggle}
                  disabled={marketMakerStatus.loading}
                  className={`w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    marketMakerStatus.running
                      ? 'bg-red-50 text-red-600 hover:bg-red-100'
                      : 'bg-green-50 text-green-600 hover:bg-green-100'
                  } disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  {marketMakerStatus.loading ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <span>Загрузка...</span>
                    </>
                  ) : marketMakerStatus.running ? (
                    <>
                      <Square className="h-4 w-4" />
                      <span>Остановить</span>
                    </>
                  ) : (
                    <>
                      <Play className="h-4 w-4" />
                      <span>Запустить</span>
                    </>
                  )}
                </button>
              </div>

              {/* User Info */}
              <button
                onClick={() => navigate('/user-settings')}
                className="w-full flex items-center gap-3 px-3 py-2 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors text-left"
              >
                <div className="p-2 bg-blue-100 rounded-full">
                  <User className="h-4 w-4 text-blue-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-gray-900 truncate">
                    {user?.username || 'User'}
                  </div>
                  <div className="text-xs text-gray-500 capitalize">
                    {user?.role || 'Viewer'} {user?.full_access && '• Full Access'}
                  </div>
                </div>
              </button>

              {/* Logout Button */}
              <button
                onClick={handleLogout}
                className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors"
              >
                <LogOut className="h-4 w-4" />
                Logout
              </button>

              <div className="text-xs text-gray-500 text-center pt-2">
                TON Blockchain Platform
                <br />
                v1.7.0 (27.11.2025)
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Mobile overlay */}
      {mobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-30 lg:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Main content */}
      <div className={`
        transition-all duration-300
        ${sidebarOpen ? 'lg:ml-72' : 'lg:ml-20'}
      `}>
        <div className="min-h-screen">
          {children}
        </div>
      </div>
    </div>
  );
};

export default Layout;
